import { queryClient } from '@/App';
import { captureNetwork, headersToRecord, newRequestId } from '@/replay/network';
import { useSessionStore } from '@/store/useSessionStore';
import { useAuthStore } from '@/store/useUser';
// test
export const baseURL = import.meta.env.VITE_API_URL as string;

const handleSessionInvalidation = (status: number, code?: string) => {
  if (status === 401 && code === 'SESSION_INVALIDATED_BY_NEW_LOGIN') {
    useAuthStore.getState().setUserData(null);
    useSessionStore.getState().setSessionInvalidatedModalOpen(true);
  }
};

/**
 * Yanıt gövdesini güvenle JSON'a çevirir. Gateway/proxy zaman aşımı veya 5xx
 * durumunda gövde HTML olabilir; bu durumda JSON.parse ham SyntaxError fırlatıp
 * asıl hatayı gizler. Bunun yerine boş nesne döner, arayan status'a göre
 * anlamlı bir hata üretir.
 */
const safeJsonParse = (text: string) => {
  if (text.length === 0) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

/**
 * Hata gövdesini (code, refNo, alan detayları) mesajın yanında taşır; çağıran
 * `err.code` / `err.body` ile ayrıntıya ulaşır. Mesaj davranışı değişmedi.
 */
export interface ApiError extends Error {
  status: number;
  code?: string;
  body?: Record<string, unknown>;
}

const buildApiError = (status: number, resp: Record<string, unknown>): ApiError => {
  // class-validator hataları dizi döner (["password is not strong enough", ...]);
  // "Sunucu hatası (400)" yerine metinler birleştirilir (30 Eki 2026 davet şifresi)
  const message =
    typeof resp.message === 'string'
      ? resp.message
      : Array.isArray(resp.message) && resp.message.length > 0
        ? resp.message.map(String).join(' · ')
        : `Sunucu hatası (${status})`;
  const err = new Error(message) as ApiError;
  err.status = status;
  if (typeof resp.code === 'string') err.code = resp.code;
  err.body = resp;
  return err;
};

export const instance = async (url: string, options?: RequestInit) => {
  const user = useAuthStore.getState().user;

  // Oturum kaydı (replay) ağ yakalama: her isteğe X-Request-Id, sonuç olay olarak kaydedilir.
  const requestId = newRequestId();
  const startedAt = performance.now();
  const fullUrl = `${baseURL}/${url}`;
  const method = options?.method ?? 'GET';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(user && user?.accessToken.token && { Authorization: `Bearer ${user.accessToken.token}` }),
    ...headersToRecord(options?.headers),
    'X-Request-Id': requestId
  };
  const reqBody = typeof options?.body === 'string' ? options.body : undefined;
  const record = (status: number, ok: boolean, resBody: string | null, resHeaders?: Headers, error?: string) =>
    captureNetwork({
      id: requestId,
      requestId,
      method,
      url: fullUrl,
      status,
      ok,
      durationMs: Math.round(performance.now() - startedAt),
      reqHeaders: headers,
      reqBody,
      resHeaders: resHeaders ? headersToRecord(resHeaders) : undefined,
      resBody,
      resType: resHeaders?.get('content-type') ?? undefined,
      error
    });

  let response: Response;
  try {
    response = await fetch(fullUrl, {
      method: 'GET',
      ...options,
      headers
    });
  } catch (e) {
    record(0, false, null, undefined, e instanceof Error ? e.message : String(e));
    throw e;
  }

  if (options?.method === 'DELETE' || response.status === 204) {
    if (!response.ok) {
      const text = await response.text();
      const resp = safeJsonParse(text);
      record(response.status, false, text, response.headers);
      handleSessionInvalidation(response.status, resp.code);
      throw buildApiError(response.status, resp);
    }
    record(response.status, true, null, response.headers);
    return response;
  }

  const text = await response.text();
  const resp = safeJsonParse(text);
  record(response.status, response.ok, text, response.headers);

  if (!response.ok) {
    handleSessionInvalidation(response.status, resp.code);
    throw buildApiError(response.status, resp);
  }

  if ('relationships' in resp) {
    const keys = Object.keys(resp.relationships);

    keys.forEach((key) => {
      if (Array.isArray(resp.relationships[key])) {
        resp.relationships[key].forEach((item: { id: number | string }) => {
          if ('id' in item) {
            queryClient.setQueryData([key, item.id], { result: item });
          }
        });
      } else {
        if ('id' in resp.relationships[key]) {
          queryClient.setQueryData(
            [key, resp.relationships[key].id],
            { result: resp.relationships[key] },
            {
              updatedAt: Date.now()
            }
          );
        }
      }
    });
  }

  return resp;
};
